import React, { Children } from 'react';
import { motion } from 'framer-motion';
import type { Slide } from './slides';
const ease = [0.22, 1, 0.36, 1] as const;
const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 18
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease
    }
  }
};
function Eyebrow({
  slide,
  index
}: {
  slide: Slide;
  index: number;
}) {
  const Icon = slide.icon;
  return <motion.div variants={item} className="flex items-center gap-3 text-accent" data-id="element-76" data-mp="2oqwrp6mbblin">
      <span className="flex h-8 w-8 items-center justify-center rounded-md border border-accent/25 bg-accent/10" data-id="element-77" data-mp="2knqpqb68gghz">
        <Icon className="h-4 w-4" strokeWidth={2.2} data-id="element-78" data-mp="2fp70o1wceole" />
      </span>
      <span className="font-mono text-xs uppercase tracking-[0.25em]" data-id="element-79" data-mp="19a64bv3eo4ea">
        {String(index + 1).padStart(2, '0')} · {slide.section}
      </span>
    </motion.div>;
}
export function SlideView({
  slide,
  index
}: {
  slide: Slide;
  index: number;
}) {
  const isTitle = slide.kind === 'title';
  return <motion.div key={slide.id} variants={container} initial="hidden" animate="show" exit={{
    opacity: 0,
    y: -12,
    transition: {
      duration: 0.25,
      ease
    }
  }} className="mx-auto flex w-full max-w-5xl flex-col justify-center px-6 py-16 sm:px-10 md:px-14" data-id="element-80" data-mp="1n5902lub1rfz">
      <Eyebrow slide={slide} index={index} data-id="element-81" data-mp="zfeo06ht86ws" />

      <div className={`mt-8 ${slide.portrait ? 'grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_270px]' : ''}`} data-id="element-82" data-mp="zdll92abi6u2">
        <div data-id="element-83" data-mp="147y5vfnj5o7e">
          <motion.h1 variants={item} className={`font-extrabold tracking-tight text-white ${isTitle ? 'text-5xl leading-[1.05] sm:text-6xl md:text-7xl' : 'text-4xl leading-[1.08] sm:text-5xl md:text-6xl'}`} data-id="element-84" data-mp="qegljsn0s7gb">
            {slide.title}
          </motion.h1>

          {slide.lead && <motion.p variants={item} className="mt-6 max-w-3xl text-lg font-medium text-accent/90 sm:text-xl" data-id="element-85" data-mp="lfwy1nss2nlu">
              {slide.lead}
            </motion.p>}

          <motion.div variants={item} className="mt-6 max-w-3xl space-y-4 text-lg leading-relaxed text-slate-400 sm:text-xl" data-id="element-86" data-mp="2idkulwwnm14a">
            {slide.body.map((p, i) => <p key={i} data-id="element-87" data-mp="29e7ewmhxsgb4" data-mpi={i}>{p}</p>)}
          </motion.div>

          {slide.metrics && <motion.div variants={item} className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.06] sm:grid-cols-4" data-id="element-88" data-mp="362dshywu6j1j">
              {slide.metrics.map(m => <div key={m.label} className="bg-panel2 px-5 py-7 sm:px-6 sm:py-8" data-id="element-89" data-mp="2s3mh4k9czbh3">
                  <div className="font-mono text-2xl font-semibold text-accent sm:text-3xl" data-id="element-90" data-mp="2s5qhwtbynud5">
                    {m.value}
                  </div>
                  <div className="mt-2 text-sm leading-snug text-slate-400" data-id="element-91" data-mp="1go7izd5eydke">
                    {m.label}
                  </div>
                </div>)}
            </motion.div>}

          {slide.mechanisms && <motion.div variants={item} className="mt-9 grid gap-3 md:grid-cols-3" aria-label="Defensible serving mechanisms" data-id="element-92" data-mp="1bjwoxjbivoqr">
              {slide.mechanisms.map((mechanism, mechanismIndex) => <article key={mechanism.title} className="rounded-lg border border-white/[0.08] bg-panel2 p-4" data-id="element-93" data-mp="1pbtepgppxmjz" data-mpi={mechanismIndex}>
                  <span className="font-mono text-xs font-semibold text-accent" data-id="element-94" data-mp="1ubxybflxaxef">
                    0{mechanismIndex + 1}
                  </span>
                  <h2 className="mt-2 text-sm font-bold text-white" data-id="element-95" data-mp="2479e8192ianr">
                    {mechanism.title}
                  </h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-400" data-id="element-96" data-mp="4x16beptrw1i">
                    {mechanism.body}
                  </p>
                </article>)}
            </motion.div>}

          {slide.footer && <motion.p variants={item} className="mt-6 border-t border-white/[0.08] pt-4 font-mono text-[11px] leading-relaxed text-slate-500" data-id="element-97" data-mp="1vctqzlvzymv9">
              {slide.footer}
            </motion.p>}
        </div>

        {slide.portrait && <motion.figure variants={item} className="mx-auto mt-2 w-full max-w-[270px] lg:mx-0" data-id="element-98" data-mp="1gn4if5lkrgky">
            <div className="overflow-hidden rounded-xl border border-accent/25 bg-panel2 p-1.5 shadow-2xl shadow-black/30" data-id="element-99" data-mp="1cm2kqt7b3cl3">
              <img src={slide.portrait.src} alt={slide.portrait.alt} className="aspect-[4/5] w-full rounded-lg object-cover object-center" data-id="element-100" data-mp="1qe8zcaut9bif" />
            </div>
            <figcaption className="mt-3 font-mono text-xs uppercase tracking-[0.18em] text-slate-500" data-id="element-101" data-mp="xvbqzfm5m3fc">
              Orion Parrott · Founder
            </figcaption>
          </motion.figure>}
      </div>
    </motion.div>;
}